import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { verdictAPI } from "@/lib/api";
import { getVerdictColor, formatPrice } from "@/lib/utils-app";
import { ClockCounterClockwise, ArrowLeft, Trash, MagnifyingGlass, FunnelSimple, X, Storefront, Spinner, Archive, Package } from "@phosphor-icons/react";
import VerdictCard from "@/components/app/VerdictCard";
import BottomNav from "@/components/app/BottomNav";

function HistoryItem({ item, onClick, onDelete }) {
  const colors = getVerdictColor(item.verdict);
  const hasThumbnail = item.thumbnail && item.thumbnail.length > 10;
  const isListed = !!item.ebay_listing_id;

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(item.id);
  };

  return (
    <div
      className="border-b-2 border-black flex items-center overflow-hidden max-w-full"
      data-testid={`history-item-${item.id}`}
    >
      <button
        onClick={onClick}
        className="flex-1 min-w-0 py-3 px-3 flex items-center gap-3 text-left hover:bg-neutral-50 transition-colors active:bg-neutral-100 overflow-hidden"
        data-testid={`history-item-btn-${item.id}`}
      >
        {/* Thumbnail or verdict badge */}
        {hasThumbnail ? (
          <div className="w-12 h-12 border-2 border-black shrink-0 overflow-hidden relative">
            <img
              src={`data:image/jpeg;base64,${item.thumbnail}`}
              alt=""
              className="w-full h-full object-cover"
            />
            <div
              className="absolute bottom-0 right-0 w-4 h-4 flex items-center justify-center text-[8px] font-black"
              style={{ backgroundColor: colors.bg, color: colors.text }}
            >
              {item.verdict === "GRAB" ? "G" : item.verdict === "CAUTION" ? "C" : "S"}
            </div>
            {isListed && (
              <div className="absolute top-0 left-0 bg-[#002FA7] p-0.5">
                <Storefront size={8} weight="fill" className="text-white" />
              </div>
            )}
          </div>
        ) : (
          <div
            className="w-12 h-12 border-2 border-black flex items-center justify-center font-heading font-black text-xs uppercase shrink-0 relative"
            style={{ backgroundColor: colors.bg, color: colors.text }}
          >
            {item.verdict === "GRAB" ? "G" : item.verdict === "CAUTION" ? "C" : "S"}
            {isListed && (
              <div className="absolute top-0 left-0 bg-[#002FA7] p-0.5">
                <Storefront size={8} weight="fill" className="text-white" />
              </div>
            )}
          </div>
        )}
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="font-heading text-sm font-bold tracking-tight truncate flex items-center gap-1.5">
            {item.title_keywords}
            {isListed && (
              <span className="inline-flex items-center gap-0.5 bg-[#002FA7] text-white px-1.5 py-0.5 text-[9px] font-bold uppercase shrink-0">
                <Storefront size={8} weight="fill" /> eBay
              </span>
            )}
          </div>
          <div className="font-body text-xs text-neutral-500 mt-0.5 truncate">
            {item.brand} &middot; {item.best_platform}
          </div>
        </div>
        <div className="text-right shrink-0 pl-2">
          <div className="font-heading text-base font-bold tracking-tight whitespace-nowrap">
            {formatPrice(item.market_data?.sold_median_price || item.list_price_low || item.list_price_high || 0)}
          </div>
          <div className="font-body text-xs text-neutral-500 whitespace-nowrap">{item.time_to_sell}</div>
        </div>
      </button>
      <button
        onClick={handleDelete}
        className="shrink-0 px-3 py-4 self-stretch flex items-center border-l-2 border-black text-neutral-400 hover:text-[#FF1744] hover:bg-red-50 transition-colors"
        data-testid={`history-delete-${item.id}`}
        title="Delete"
      >
        <Trash size={18} weight="bold" />
      </button>
    </div>
  );
}

const FILTER_OPTIONS = [
  { key: "all", label: "All" },
  { key: "unlisted", label: "Unlisted" },
  { key: "listed", label: "Listed on eBay" },
  { key: "grab", label: "Grab" },
  { key: "caution", label: "Caution" },
  { key: "skip", label: "Skip" },
];

const PAGE_SIZE = 50;

export default function HistoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedVerdict, setSelectedVerdict] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searching, setSearching] = useState(false);
  const [filter, setFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const searchTimeout = useRef(null);
  const sentinelRef = useRef(null);

  // Load initial history
  const loadHistory = useCallback(async () => {
    try {
      const { data } = await verdictAPI.history(PAGE_SIZE, 0);
      setItems(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  // Load more (infinite scroll)
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || searchResults !== null) return;
    setLoadingMore(true);
    try {
      const { data } = await verdictAPI.history(PAGE_SIZE, items.length);
      setItems((prev) => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } catch {
      // ignore
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, items.length, searchResults]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current || searchResults !== null) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: "200px" }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore, searchResults]);

  // Server-side search with debounce
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    const q = search.trim();
    if (!q) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const { data } = await verdictAPI.search(q);
        setSearchResults(data);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [search]);

  const handleDelete = async (verdictId) => {
    setDeleting(verdictId);
    try {
      await verdictAPI.delete(verdictId);
      setItems((prev) => prev.filter((i) => i.id !== verdictId));
      if (searchResults) setSearchResults((prev) => prev.filter((i) => i.id !== verdictId));
      if (selectedVerdict?.id === verdictId) setSelectedVerdict(null);
    } catch {
      // ignore
    } finally {
      setDeleting(null);
    }
  };

  // Determine display list: search results or paginated items, then apply filter
  const displayItems = useMemo(() => {
    const source = searchResults !== null ? searchResults : items;
    if (filter === "all") return source;
    if (filter === "unlisted") return source.filter((i) => !i.ebay_listing_id);
    if (filter === "listed") return source.filter((i) => !!i.ebay_listing_id);
    if (filter === "grab") return source.filter((i) => i.verdict === "GRAB");
    if (filter === "caution") return source.filter((i) => i.verdict === "CAUTION");
    if (filter === "skip") return source.filter((i) => i.verdict === "SKIP");
    return source;
  }, [items, searchResults, filter]);

  const totalLoaded = searchResults !== null ? searchResults.length : items.length;

  // Detail view for a selected verdict
  if (selectedVerdict) {
    return (
      <div className="min-h-screen bg-white pb-32">
        <div className="sticky top-0 z-50 bg-white border-b-4 border-black py-4 px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedVerdict(null)}
              className="border-2 border-black p-2 active:translate-y-[1px] transition-transform"
              data-testid="verdict-back-btn"
            >
              <ArrowLeft size={20} weight="bold" />
            </button>
            <h1 className="font-heading text-2xl font-black tracking-tighter uppercase">
              Verdict
            </h1>
          </div>
          <button
            onClick={() => handleDelete(selectedVerdict.id)}
            className="border-2 border-black p-2 text-[#FF1744] hover:bg-red-50 active:translate-y-[1px] transition-all"
            data-testid="verdict-detail-delete-btn"
          >
            <Trash size={20} weight="bold" />
          </button>
        </div>

        <div className="px-4 py-6">
          <VerdictCard verdict={selectedVerdict} />
        </div>

        <BottomNav />
      </div>
    );
  }

  const activeFilter = filter !== "all";
  const isSearching = searchResults !== null;

  return (
    <div className="min-h-screen bg-white pb-24">
      <div className="sticky top-0 z-50 bg-white border-b-4 border-black">
        <div className="py-4 px-6 flex items-center justify-between">
          <h1 className="font-heading text-2xl font-black tracking-tighter uppercase">
            History
          </h1>
          <div className="flex items-center gap-2 font-body text-xs text-neutral-500">
            {totalLoaded > 0 && (
              <span>
                {displayItems.length !== totalLoaded ? `${displayItems.length} of ` : ""}
                {totalLoaded}{isSearching ? " found" : ""}{!isSearching && hasMore ? "+" : ""}
              </span>
            )}
          </div>
        </div>

        {/* Search + Filter bar */}
        {(totalLoaded > 0 || search) && (
          <div className="px-4 pb-3 flex gap-2">
            <div className="flex-1 relative">
              <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search all items..."
                className="w-full pl-8 pr-8 py-2 border-2 border-black font-body text-sm"
                data-testid="history-search"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400"
                >
                  {searching ? <Spinner size={14} weight="bold" className="animate-spin" /> : <X size={14} weight="bold" />}
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(true)}
              className={`shrink-0 p-2 border-2 border-black flex items-center gap-1 font-heading text-xs font-bold uppercase ${
                activeFilter ? "bg-black text-white" : "hover:bg-neutral-50"
              }`}
              data-testid="history-filter-btn"
            >
              <FunnelSimple size={14} weight="bold" />
              {activeFilter && <span>{FILTER_OPTIONS.find((f) => f.key === filter)?.label}</span>}
            </button>
          </div>
        )}

        {/* Archived Bulk Sessions link — hidden in iter_45. Route still alive at /__hidden/bulk-archive. */}
        {false && (
        <Link
          to="/bulk-archive"
          className="flex items-center justify-between px-4 py-2 border-t-2 border-black hover:bg-neutral-50 active:bg-neutral-100"
          data-testid="archived-bulk-link"
        >
          <div className="flex items-center gap-2">
            <Archive size={16} weight="bold" className="text-neutral-600" />
            <span className="font-heading text-xs font-bold uppercase tracking-wide">Archived Bulk Sessions</span>
          </div>
          <span className="font-body text-[10px] text-neutral-400">→</span>
        </Link>
        )}

        {/* Inventory Manager — hidden in iter_48. Route still alive at /inventory. */}
        {false && (
        <Link
          to="/inventory"
          className="hidden md:flex items-center justify-between px-4 py-2 border-t-2 border-black hover:bg-neutral-50 active:bg-neutral-100"
          data-testid="inventory-manager-link"
        >
          <div className="flex items-center gap-2">
            <Package size={16} weight="bold" className="text-neutral-600" />
            <span className="font-heading text-xs font-bold uppercase tracking-wide">Inventory Manager</span>
            <span className="font-body text-[10px] text-neutral-400 normal-case">· Desktop</span>
          </div>
          <span className="font-body text-[10px] text-neutral-400">→</span>
        </Link>
        )}
      </div>

      {loading ? (
        <div className="p-8 text-center font-body text-neutral-500">Loading...</div>
      ) : items.length === 0 && !search ? (
        <div className="p-8 text-center" data-testid="history-empty">
          <ClockCounterClockwise size={48} weight="bold" className="mx-auto text-neutral-300 mb-4" />
          <div className="font-heading text-xl font-bold tracking-tight uppercase mb-2">
            No Scans Yet
          </div>
          <p className="font-body text-sm text-neutral-500">
            Scan your first item to see the verdict history
          </p>
        </div>
      ) : displayItems.length === 0 ? (
        <div className="p-8 text-center">
          {searching ? (
            <Spinner size={36} weight="bold" className="mx-auto text-neutral-300 mb-3 animate-spin" />
          ) : (
            <MagnifyingGlass size={36} weight="bold" className="mx-auto text-neutral-300 mb-3" />
          )}
          <div className="font-heading text-base font-bold uppercase mb-1">
            {searching ? "Searching..." : "No Results"}
          </div>
          {!searching && <p className="font-body text-sm text-neutral-500">Try different search terms or filters</p>}
        </div>
      ) : (
        <div data-testid="history-list" className="overflow-hidden">
          {displayItems.map((item) => (
            <HistoryItem
              key={item.id}
              item={item}
              onClick={() => setSelectedVerdict(item)}
              onDelete={handleDelete}
            />
          ))}
          {/* Infinite scroll sentinel */}
          {!isSearching && hasMore && (
            <div ref={sentinelRef} className="p-4 text-center">
              {loadingMore && (
                <div className="flex items-center justify-center gap-2 font-body text-xs text-neutral-500">
                  <Spinner size={14} weight="bold" className="animate-spin" />
                  Loading more...
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filter Modal */}
      {showFilters && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center pb-[72px] sm:pb-0">
          <div className="bg-white border-t-4 sm:border-4 border-black w-full sm:max-w-sm sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <div className="flex items-center justify-between p-4 border-b-2 border-black">
              <h2 className="font-heading text-base font-black uppercase">Filter</h2>
              <button onClick={() => setShowFilters(false)} className="p-1">
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="p-2">
              {FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => { setFilter(opt.key); setShowFilters(false); }}
                  className={`w-full p-3 text-left font-body text-sm flex items-center justify-between ${
                    filter === opt.key ? "bg-black text-white" : "hover:bg-neutral-50"
                  }`}
                  data-testid={`filter-${opt.key}`}
                >
                  {opt.label}
                  {opt.key === "listed" && <Storefront size={14} weight="bold" />}
                  {filter === opt.key && <span className="font-bold">&#10003;</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
